/* eslint-disable react-hooks/exhaustive-deps */
import { FunctionComponent as F, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import IconButton from "@mui/material/IconButton";
import { PROTOCOL } from "../../core/router/paths";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { retrieveListingFromStore } from "../../core/store/utils";
import { getListing } from "../../core/http/listing";
import { validateVendorBaseTax, validateVendorTaxPercent } from "../../utils/validate";
import { updateTax } from "../../core/http/vendor";
import { Typography } from "@mui/material";


interface LocationState
{
  listing?: { listingId: number, listingCurrencySymbol: string, baseTax?: number, taxPercent?: number }
}

interface Params
{
  vendorId?: string,
  protocolId?: string
}

const SubmitTax: F = () =>
{
  const navigate = useNavigate();
  const state = useLocation().state as LocationState | undefined;
  const params: Params = useParams();
  const vendorId = params.vendorId;
  const listingId = state?.listing?.listingId;
  let listing = state?.listing;

  useEffect(() =>
  {
    if(!listingId)
      return;

    // Try retrieve listing (for currency symbol) from store
    listing = retrieveListingFromStore(listingId);

    // Request listing from server
    // (if someone has opened a shared link with a listingId not amongst their current store)
    (async() => listing = (await getListing(listingId)) || undefined)()
      .then(() =>
      {
        if(!listing)
          navigate(-1);
      });
  }, []);

  const [baseTax, setBaseTax] = useState(listing?.baseTax?.toFixed(2) || "");
  const [taxPercent, setTaxPercent] = useState(listing?.taxPercent?.toFixed(1) || "");

  const [vendorChecked, setVendorChecked] = useState(false);

  if(!vendorId)
  {
    navigate(PROTOCOL);
    return <></>;
  }

  const [message, setMessage] = useState("");

  const handleSubmit = async() =>
  {
    if(!validateVendorBaseTax(baseTax) || !validateVendorTaxPercent(taxPercent))
      return;

    updateTax(
      Number(vendorId),
      { baseTax: Number(baseTax), taxPercent: Number(taxPercent) },
      vendorChecked ? undefined : listingId);

    setMessage("Request sent. Tax will be updated once this has been approved.");
  };

  const handleOk = async() =>
  {
    navigate(-1);
  };

  return (
    <Dialog
      maxWidth='md'
      fullWidth
      PaperProps={{ sx: { p: '15px' }}}
      open={true}
      onClose={() => navigate(PROTOCOL + (params.protocolId ? "/" + params.protocolId : ""))}>

      <IconButton
        disableRipple
        onClick={() => navigate(-1)}
        sx={{ display: 'inline-flex', float: 'left', width: '80px' }}>
        <ArrowBackIosNewIcon sx={{ mt: 3, ml: 1, fontSize: 65, color: 'lightGray' }}/>
      </IconButton>

      <DialogTitle sx={{ ml: 3, fontSize: '30px' }}>Submit Tax Update</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ width: '750px', pt: 1 }}>

          <Typography variant="body2">
            {"Base tax represents any fixed amount per item or bundle, " +
            "and the tax percent accounts for taxes which increase with price. " +
            "Both values are on top of what is listed on the product page - " +
            "typically import taxes are shown at checkout. " +
            "(Note that some sites may also change the price on the product page " +
            "based on the user's country.) "}
          </Typography>
          <Typography variant="body2">
            {"Submitting values here will send them to me for review. " +
            "I'll then add them to the database or follow up with you for more info. " +
            "International tax rates can be unclear at times! "}
          </Typography>

          <FormControl>
            <InputLabel htmlFor='outlined-adornment-amount'>
              {'Base tax'}
            </InputLabel>
            <OutlinedInput
              size='small'
              sx={{ pt: '6px' }}
              startAdornment={
                <InputAdornment position='start'>
                  {listing?.listingCurrencySymbol || ""}
                </InputAdornment>}
              label='Base tax'
              value={baseTax}
              onChange={event => setBaseTax(event.target.value)}
              error={!validateVendorBaseTax(baseTax)}/>
          </FormControl>

          <FormControl>
            <InputLabel htmlFor='outlined-adornment-amount'>
              {'Tax percent on listing price'}
            </InputLabel>
            <OutlinedInput
              size='small'
              sx={{ pt: '6px' }}
              startAdornment={
                <InputAdornment position='start'>
                  {"%"}
                </InputAdornment>}
              label='Tax percent on listing price'
              value={taxPercent}
              onChange={event => setTaxPercent(event.target.value)}
              error={!validateVendorTaxPercent(taxPercent)}/>
          </FormControl>

          <FormControl component='fieldset'>
            <FormControlLabel
              label={'Applies to all listings from this vendor which ship to your country'}
              control={<Checkbox
                checked={vendorChecked}
                onChange={() => setVendorChecked(!vendorChecked)}
                inputProps={{ 'aria-label': 'controlled' }}/>}/>
          </FormControl>

          <Typography variant="body2">
            {message}
          </Typography>

          <Button
            variant='contained'
            sx={{ width: '120px' }}
            onClick={message ? handleOk : handleSubmit}>
            {message ? "Ok" : "Submit"}
          </Button>

        </Stack>
      </DialogContent>
    </Dialog>);
};


export default SubmitTax;
